import { pageScrollY } from "@/lib/ui/pinned-body";

const LAST_SCROLL_KEY = "hiive:last-route-scroll";
/**
 * How many pages keep a remembered position.
 *
 * This used to be a single record, which meant any navigation at all erased
 * it: walk from a list into a profile, tap something there, and the two back
 * presses home found the list's position already overwritten by the profile's.
 * A handful of entries covers every real back stack; the cap is what keeps
 * sessionStorage from growing for the life of the tab.
 */
const MAX_REMEMBERED_ROUTES = 8;
const PENDING_SCROLL_KEY = "hiive:pending-route-scroll";
const PENDING_RAIL_KEY = "hiive:pending-rail-scroll";

// Some embedded browsers expose Window but deny access to sessionStorage.
// Scroll restoration is optional; navigation must still work in that case.
function readSessionItem(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSessionItem(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage access and quota failures only disable this restoration.
  }
}

function removeSessionItem(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Storage access may disappear during an embedded browser navigation.
  }
}

type RouteScroll = {
  href: string;
  x: number;
  y: number;
  /**
   * Horizontal rails on the page, by restore key. On a page whose content is
   * carousels rather than a long list — /discover's Recommended and By Activity
   * tabs are the extreme case — window.scrollY barely moves, so restoring only
   * x/y lands you at the top of a rail you had swiped halfway through.
   */
  rails?: Record<string, number>;
};

type RailScroll = {
  href: string;
  stagedAt: number;
  rails: Record<string, number>;
};

/**
 * How long a staged rail position stays claimable. Rails sit behind Suspense
 * and their own queries, so they can mount well after the route does — but not
 * minutes later. Without a bound, a record nobody claimed (Next kept the route
 * mounted, so no rail ever remounted to read it) would sit in sessionStorage
 * and yank a rail whenever one happened to be rebuilt on that href.
 */
const RAIL_RESTORE_TTL_MS = 10_000;

/**
 * A rail reports a single number: whatever it needs to be handed back to look
 * untouched. Plain overflow-x containers report scrollLeft; snap carousels
 * report their logical index, because their scrollLeft depends on measurements
 * that do not exist until after the remount. `read` returns null when the rail
 * is not the one on screen — Next keeps navigated-away routes mounted in hidden
 * subtrees, and their stale rails must not overwrite the live page's entry.
 */
type ScrollRail = {
  key: string;
  read: () => number | null;
};

const scrollRails = new Set<ScrollRail>();

export function registerScrollRail(rail: ScrollRail) {
  scrollRails.add(rail);
  return () => {
    scrollRails.delete(rail);
  };
}

function snapshotRails() {
  const rails: Record<string, number> = {};

  for (const rail of scrollRails) {
    const value = rail.read();
    if (value === null || !Number.isFinite(value)) continue;
    rails[rail.key] = value;
  }

  return rails;
}

function parseRails(value: unknown): Record<string, number> | undefined {
  if (!value || typeof value !== "object") return undefined;
  const rails: Record<string, number> = {};

  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (typeof entry === "number" && Number.isFinite(entry)) {
      rails[key] = entry;
    }
  }

  return Object.keys(rails).length > 0 ? rails : undefined;
}

function parseRouteScroll(value: string | null): RouteScroll | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<RouteScroll>;
    if (
      typeof parsed.href !== "string" ||
      typeof parsed.x !== "number" ||
      typeof parsed.y !== "number"
    ) {
      return null;
    }
    return {
      href: parsed.href,
      x: parsed.x,
      y: parsed.y,
      rails: parseRails(parsed.rails),
    };
  } catch {
    return null;
  }
}

function parseRailScroll(value: string | null): RailScroll | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<RailScroll>;
    if (typeof parsed.href !== "string") return null;
    if (typeof parsed.stagedAt !== "number") return null;
    const rails = parseRails(parsed.rails);
    if (!rails) return null;
    return { href: parsed.href, stagedAt: parsed.stagedAt, rails };
  } catch {
    return null;
  }
}

function currentHref() {
  return `${window.location.pathname}${window.location.search}`;
}

/**
 * Remembered positions, oldest first — the order is the eviction order.
 * Tolerates the old single-record shape so an open tab mid-deploy degrades to
 * "one position remembered" rather than throwing.
 */
function readRemembered(): RouteScroll[] {
  const raw = readSessionItem(LAST_SCROLL_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    const entries = Array.isArray(parsed) ? parsed : [parsed];
    return entries
      .map((entry) => parseRouteScroll(JSON.stringify(entry)))
      .filter((entry): entry is RouteScroll => entry !== null);
  } catch {
    return [];
  }
}

function writeRemembered(entries: RouteScroll[]) {
  writeSessionItem(
    LAST_SCROLL_KEY,
    JSON.stringify(entries.slice(-MAX_REMEMBERED_ROUTES)),
  );
}

function rememberedFor(href: string): RouteScroll | null {
  return readRemembered().find((entry) => entry.href === href) ?? null;
}

export function captureCurrentRouteScroll() {
  if (typeof window === "undefined") return;
  const value: RouteScroll = {
    href: currentHref(),
    x: window.scrollX,
    y: pageScrollY(),
    rails: snapshotRails(),
  };
  const entries = readRemembered().filter((entry) => entry.href !== value.href);
  entries.push(value);
  writeRemembered(entries);
}

function stageRails(saved: RouteScroll) {
  if (!saved.rails) {
    removeSessionItem(PENDING_RAIL_KEY);
    return;
  }

  const value: RailScroll = {
    href: saved.href,
    stagedAt: Date.now(),
    rails: saved.rails,
  };
  writeSessionItem(PENDING_RAIL_KEY, JSON.stringify(value));
}

export function prepareRouteScrollRestore(targetHref: string) {
  if (typeof window === "undefined") return;
  const saved = rememberedFor(targetHref);
  if (!saved) return;
  writeSessionItem(PENDING_SCROLL_KEY, JSON.stringify(saved));
  stageRails(saved);
}

export function consumePendingRouteScroll(
  currentPageHref: string,
): RouteScroll | null {
  if (typeof window === "undefined") return null;
  const pending = parseRouteScroll(readSessionItem(PENDING_SCROLL_KEY));
  if (!pending || pending.href !== currentPageHref) return null;
  removeSessionItem(PENDING_SCROLL_KEY);
  return pending;
}

/**
 * Read back one rail's saved position and drop it, so a rail restores once per
 * back navigation. Rails mount long after <RouteScrollRestorer /> runs — they
 * sit behind Suspense and their own queries — so each one pulls its own value
 * on mount instead of being pushed one centrally.
 */
export function consumeRailScroll(key: string): number | null {
  if (typeof window === "undefined") return null;
  const pending = parseRailScroll(readSessionItem(PENDING_RAIL_KEY));
  if (!pending || pending.href !== currentHref()) return null;

  if (Date.now() - pending.stagedAt > RAIL_RESTORE_TTL_MS) {
    removeSessionItem(PENDING_RAIL_KEY);
    return null;
  }

  const value = pending.rails[key];
  if (typeof value !== "number") return null;

  delete pending.rails[key];
  if (Object.keys(pending.rails).length === 0) {
    removeSessionItem(PENDING_RAIL_KEY);
  } else {
    writeSessionItem(PENDING_RAIL_KEY, JSON.stringify(pending));
  }

  // Drop it from the capture too, so a restore cannot be staged a second time
  // and yank a rail the reader has since scrolled themselves. Both back paths
  // stage from the capture, and popstate can fire after the rail has already
  // mounted and read its position.
  const entries = readRemembered();
  const captured = entries.find((entry) => entry.href === pending.href);
  if (captured?.rails?.[key] !== undefined) {
    delete captured.rails[key];
    writeRemembered(entries);
  }

  return value;
}
